import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../core/redux/api/reduxStandardApiAction';
import { apiGetValidLiftAccessLocations } from '../../../core/redux/api/endpoints';
import { IAppState } from '../../../core/redux/IAppState';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../core/redux/api/module';
import { ACTION_TYPE } from '../../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../../core/redux/api/circuitBreaker/module';
import { LiftAccessValidLocations, LiftAccessValidLocationsEntry } from './liftAccessValidLocations';
import { assoc } from '../../../language/module';
import * as R from 'ramda';


export const KEY_ACTION = ACTION_TYPE.VALID_LIFT_ACCESS_LOCATIONS_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.VALID_LIFT_ACCESS_LOCATIONS_REQUEST,
  ACTION_TYPE.VALID_LIFT_ACCESS_LOCATIONS_RESPONSE,
  ACTION_TYPE.VALID_LIFT_ACCESS_LOCATIONS_ERROR,
];


/**
 * Key under which a resort's valid locations for a single entitlement type are cached in app state. Keyed by the
 * resort abbreviation we requested (not the one echoed by the API) so lookups stay consistent regardless of any
 * server-side formatting of the abbreviation.
 */
export const validLocationsKey = (resortAbbreviation: string, entitlementTypeId: number): string =>
  `${resortAbbreviation}-${entitlementTypeId}`;


/**
 * A stable id for a batched request so identical concurrent requests bail. Order/duplicate independent.
 */
const requestIdFor = (resortAbbreviation: string, entitlementTypeIds: number[]): string =>
  `${resortAbbreviation}:${R.uniq(entitlementTypeIds).sort((a, b) => a - b).join(',')}`;


/**
 * Constructs an action to fetch the valid lift access locations for a batch of entitlement type ids at a resort.
 * The endpoint returns one entry per requested type id, so callers should pass every restricted type id they need
 * in a single call to minimize network requests.
 * @param resortAbbreviation - the resort whose location rules we're resolving against
 * @param entitlementTypeIds - the entitlement type ids to resolve locations for
 */
export const getValidLiftAccessLocationsAction = (
  resortAbbreviation: string,
  entitlementTypeIds: number[],
) => {
  const distinctTypeIds = R.uniq(entitlementTypeIds);
  const requestId = requestIdFor(resortAbbreviation, distinctTypeIds);

  return makeApiAction(
    {
      method:   'GET',
      headers:  (state: IAppState) => ({
        'content-type':  'application/json',
        'Authorization': `Bearer ${state.auth.bearerToken}`,
      }),
      endpoint: apiGetValidLiftAccessLocations(resortAbbreviation, distinctTypeIds),
      types:    ACTIONS.map(action => ({
        type: action,
        meta: { resortAbbreviation, requestId, entitlementTypeIds: distinctTypeIds },
      })) as RsaaTypes,
      bailout:  (state: IAppState) => !!getLoading(state, requestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
    },
  );
};


const reducer: ApiReducer<LiftAccessValidLocations[]> = (state, action, error, isLoading, payload) => {
  const { resortAbbreviation, requestId, entitlementTypeIds } = action.meta;
  const requestedTypeIds: number[] = entitlementTypeIds || [];

  if (isLoading) {
    return addLoading(state, requestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    // Cache an error entry for every requested id so they aren't re-requested on the next state change
    // (the effect only refetches ids missing from the cache), and so the tooltip can show an error state.
    const erroredState = addError(withBreaker, requestId, KEY_ACTION, error);
    const erroredMap = requestedTypeIds.reduce(
      (map, typeId) => map.add(validLocationsKey(resortAbbreviation, typeId), { status: 'error' } as LiftAccessValidLocationsEntry),
      erroredState.validLiftAccessLocations,
    );
    return assoc('validLiftAccessLocations', erroredMap, erroredState);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, requestId, KEY_ACTION);

  const payloadByType = R.indexBy(item => String(item.entitlementTypeId), payload || []);

  // Cache an entry for every requested id (keyed by resort + type so it's reusable across guests). Ids the API
  // didn't return get a synthetic not-restricted entry so they're never re-requested even if the response omits them.
  const updatedMap = requestedTypeIds.reduce(
    (map, typeId) => {
      const data: LiftAccessValidLocations = payloadByType[String(typeId)] ?? {
        entitlementTypeId: typeId,
        resortAbbreviation,
        isLocationRestricted: false,
        validLocations: [],
      };
      return map.add(validLocationsKey(resortAbbreviation, typeId), { status: 'loaded', data } as LiftAccessValidLocationsEntry);
    },
    noLoadingOrError.validLiftAccessLocations,
  );

  return assoc('validLiftAccessLocations', updatedMap, noLoadingOrError);
};


export const getValidLiftAccessLocationsReducer = makeApiReducer<LiftAccessValidLocations[]>(ACTIONS, KEY_ACTION, reducer);
